import {
  ApiError,
  DriverEarningsComparisonService,
  DriverEarningsDebugService,
  DriverEarningsRecalculationService,
  DriverEarningsRecoveryService,
  HistoricalDriverEarningsReconstructionService,
  RegionDriverEarningSettingsService,
  type DriverEarningComparisonAdminDto,
  type DriverEarningComparisonJobResponseDto1OfDriverEarningOpenTerminalRecoveryResultDtoAdmin,
  type DriverEarningComparisonJobResponseDto1OfDriverEarningReconstructedComparisonSummaryDtoAdmin,
  type DriverEarningComparisonJobResponseDto1OfDriverEarningStoredComparisonSummaryDtoAdmin,
  type DriverEarningComparisonJobResponseDto1OfNullable_1Admin as DriverEarningComparisonJobStateResponseAdmin,
  type DriverEarningDebugAdminDto,
  type DriverEarningOpenTerminalRecoveryRequestAdmin,
  type DriverEarningOpenTerminalRecoverySummaryDtoAdmin,
  type DriverEarningReconstructedComparisonSummaryRequestAdmin,
  type DriverEarningRecalculateSourceAdminDto,
  type DriverEarningStoredComparisonSummaryRequestAdmin,
  type DriverSalaryBatchDebugAdminDto,
  type HistoricalDriverEarningPreviewAdminDto,
  type PostDriverEarningRecalculateSourceAdminRequest,
  type PostHistoricalDriverEarningPreviewAdminRequest,
  type TaskSourceType
} from '@/backend/market'
import type { RegionDriverEarningSettingEntity } from '../../domain/entities/RegionDriverEarningSettingEntity'
import { DriverEarningSettingRepository } from '../../domain/repositories/DriverEarningSettingRepository'
import { RegionDriverEarningSettingModel } from '../models/RegionDriverEarningSettingModel'

export class DriverEarningSettingRepositoryImpl implements DriverEarningSettingRepository {
  async getRegionDriverEarningSettings(): Promise<
    RegionDriverEarningSettingEntity[] | { error: true }
  > {
    try {
      const response = await RegionDriverEarningSettingsService.getAdmin()
      return response.map((item) => new RegionDriverEarningSettingModel(item))
    } catch {
      return { error: true }
    }
  }

  async updateRegionDriverEarningSetting(
    regionId: number,
    data: RegionDriverEarningSettingEntity
  ): Promise<RegionDriverEarningSettingEntity | { error: true }> {
    try {
      const response = await RegionDriverEarningSettingsService.putAdmin({
        regionId,
        requestBody: {
          multiDropoffDiscountPercentage: data.multiDropoffDiscountPercentage ?? 0
        }
      })
      return new RegionDriverEarningSettingModel(response)
    } catch {
      return { error: true }
    }
  }

  async recalculateSource(
    data: PostDriverEarningRecalculateSourceAdminRequest
  ): Promise<DriverEarningRecalculateSourceAdminDto | { error: true }> {
    try {
      return await DriverEarningsRecalculationService.recalculateSourceAdmin({
        requestBody: data
      })
    } catch {
      return { error: true }
    }
  }

  async previewHistoricalEarnings(
    data: PostHistoricalDriverEarningPreviewAdminRequest
  ): Promise<HistoricalDriverEarningPreviewAdminDto | { error: true }> {
    try {
      return await HistoricalDriverEarningsReconstructionService.previewAdmin({
        requestBody: data
      })
    } catch {
      return { error: true }
    }
  }

  async getEarningBySource(
    sourceType: TaskSourceType,
    sourceId: string
  ): Promise<DriverEarningDebugAdminDto | { error: true } | { notFound: true }> {
    try {
      return await DriverEarningsDebugService.getEarningBySourceAdmin({
        sourceType,
        sourceId
      })
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) {
        return { notFound: true }
      }
      return { error: true }
    }
  }

  async getBatch(
    batchId: string
  ): Promise<DriverSalaryBatchDebugAdminDto | { error: true } | { notFound: true }> {
    try {
      return await DriverEarningsDebugService.getBatchAdmin({
        batchId
      })
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) {
        return { notFound: true }
      }
      return { error: true }
    }
  }

  async getDriverEarnings(
    driverId: number,
    from?: string,
    to?: string,
    sourceType?: TaskSourceType
  ): Promise<DriverEarningDebugAdminDto[] | { error: true }> {
    try {
      return await DriverEarningsDebugService.getDriverEarningsAdmin({
        driverId,
        from,
        to,
        sourceType
      })
    } catch {
      return { error: true }
    }
  }

  async getComparison(
    sourceType: TaskSourceType,
    sourceId: string
  ): Promise<DriverEarningComparisonAdminDto | { error: true } | { notFound: true }> {
    try {
      return await DriverEarningsDebugService.getComparisonAdmin({
        sourceType,
        sourceId
      })
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) {
        return { notFound: true }
      }
      return { error: true }
    }
  }

  async startStoredComparisonSummary(
    data: DriverEarningStoredComparisonSummaryRequestAdmin
  ): Promise<DriverEarningComparisonJobResponseDto1OfDriverEarningStoredComparisonSummaryDtoAdmin | { error: true }> {
    try {
      return await DriverEarningsComparisonService.postStoredSummaryAsyncAdmin({
        requestBody: data
      })
    } catch {
      return { error: true }
    }
  }

  async startReconstructedComparisonSummary(
    data: DriverEarningReconstructedComparisonSummaryRequestAdmin
  ): Promise<DriverEarningComparisonJobResponseDto1OfDriverEarningReconstructedComparisonSummaryDtoAdmin | { error: true }> {
    try {
      return await DriverEarningsComparisonService.postReconstructedSummaryAsyncAdmin({
        requestBody: data
      })
    } catch {
      return { error: true }
    }
  }

  async getComparisonJobState(
    requestKey: string
  ): Promise<DriverEarningComparisonJobStateResponseAdmin | { error: true }> {
    try {
      return await DriverEarningsComparisonService.getJobStateAsyncAdmin({
        requestKey
      })
    } catch {
      return { error: true }
    }
  }

  async getOpenTerminalRecoverySummary(data: {
    regionIds?: number[]
    driverIds?: number[]
    batchIds?: string[]
    shipmentStatuses?: number[]
    terminalFromUtc?: string
    terminalToUtc?: string
    page?: number
    pageSize?: number
  }): Promise<DriverEarningOpenTerminalRecoverySummaryDtoAdmin | { error: true }> {
    try {
      return await DriverEarningsRecoveryService.getOpenTerminalBatchesAsyncAdmin(data)
    } catch {
      return { error: true }
    }
  }

  async continueOpenTerminalRecovery(
    data: DriverEarningOpenTerminalRecoveryRequestAdmin
  ): Promise<DriverEarningComparisonJobResponseDto1OfDriverEarningOpenTerminalRecoveryResultDtoAdmin | { error: true }> {
    try {
      return await DriverEarningsRecoveryService.continueOpenTerminalBatchesAsyncAdmin({
        requestBody: data
      })
    } catch {
      return { error: true }
    }
  }

  async getRecoveryJobState(
    requestKey: string
  ): Promise<DriverEarningComparisonJobStateResponseAdmin | { error: true }> {
    try {
      return await DriverEarningsRecoveryService.getJobStateAsyncAdmin({
        requestKey
      })
    } catch {
      return { error: true }
    }
  }
}
